import { Role } from "@/types/roles";
import { COUNTRY_CODES } from "@/utils/countryCodes";
import toast from "react-hot-toast";
import * as Yup from "yup";
import {
  Image as ImageIcon,
  FileText,
  FileSpreadsheet,
  FileVideo,
  File,
  Download
} from "lucide-react";


export const DEFAULT_PAGE_SIZE = 10;

export function formatAsTitleCase(str:string){
    return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function formatFullName(user:{firstName?:string, lastName?:string}){
    return `${user?.firstName} ${user?.lastName}`;
}

export function formatDate(dateStr:string){
  if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

export function formatRole(role:Role){
    switch(role){
        case Role.SALES_AGENT:
            return 'Sales Agent';
        case Role.ADMIN:
            return 'Admin';
        case Role.CALLING_AGENT:
            return 'Calling Agent';
        default:
            return role;
    }
}



export interface DateRangeFilter {
    preset: '7' | '30' | '90' | 'custom';
    dateFrom: string; 
    dateTo: string;   
  };
  
  export interface ListingFilters {
    dateRange?: DateRangeFilter;
  };

  export type BuiltRangeFilter = {
    dateRangeType?: 'dateRange' | 'lastxdays';
    startDate?: string;
    endDate?: string;
    day?: number;
  };

  const toDateOnlyString = (value: string) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };
 
  export const buildRangeFilter = (dr?: DateRangeFilter): BuiltRangeFilter => {
    if (!dr) return {};
    if (dr.preset === 'custom') {
      return {
        dateRangeType: 'dateRange',
        startDate: toDateOnlyString(dr.dateFrom),
        endDate: toDateOnlyString(dr.dateTo),
      };
    }
    return {
      dateRangeType: 'lastxdays',
      day: Number(dr.preset), 
    };
  };
  

  export const backToLogin = () => {
    window.location.href = '/login';
  }

  export const getPhoneValidation = (countryCode:string) => {
  const country = COUNTRY_CODES.find(c => c.code === countryCode);

  // Default fallback if code not found
  const min = country?.minDigits ?? 6;
  const max = country?.maxDigits ?? 15;

  return Yup.string()
    .matches(/^\d+$/, "Phone number must contain only digits")
    .min(min, `Phone number must be at least ${min} digits`)
    .max(max, `Phone number must be at most ${max} digits`)
    .required("Phone is required");
};

export const isAdmin = (role: Role) => {
  return role === Role.ADMIN;
}

/** @deprecated use isAdmin */
export const isAdminOrTenantAdmin = isAdmin;


export function checkForOnlyPositiveNumbers(value: string) {
  return /^[0-9]*\.?[0-9]+$/.test(value) || value === "";
}
export const groupedFileTypes = {
  Images: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  Documents: ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx'],
  Videos: ['mp4', 'mpeg', 'mov'],
};

export const ALL_ALLOWED_EXTENSIONS = Object.values(groupedFileTypes).flat();

/**
 * Returns true if file extension is allowed
 */
export function isAllowedFileType(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return ALL_ALLOWED_EXTENSIONS.includes(ext);
}

/**
 * Filters files that match allowed types
 */
export function filterAllowedFiles(files: File[]): File[] {
  return files.filter((file) => isAllowedFileType(file));
}

export const allowOnly1to100 = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const invalid = ["+", "-", "e", "E", ".", ",", " "];
  if (invalid.includes(e.key)) {
    e.preventDefault();
  }
};

export const filterAllowedFilesWithError = (files: File[]) => {
  const files1 = Array.from(files || []);
    files1.map((f)=>{
      if(!isAllowedFileType(f)){
          toast.error(`File type not allowed: ${f.name}`)
      }
    })
    const files3= filterAllowedFiles(files1);
    return files3;
}

export const blockSpecialChars = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const invalid = ["+", "-", "e", "E", ".", ",", " "];
  if (invalid.includes(e.key)) e.preventDefault();
};

export function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return null;
}


export const fileTypeIconMap: Record<string, any> = {
  IMAGE: ImageIcon,
  PNG: ImageIcon,
  JPG: ImageIcon,
  JPEG: ImageIcon,

  PDF: FileText,
  DOC: FileText,
  DOCX: FileText,
  TXT: FileText,

  XLS: FileSpreadsheet,
  XLSX: FileSpreadsheet,

  VIDEO: FileVideo,

  OTHER: File,
};
// export const downloadFile = async (url: string, fileName: string) => {
//   const res = await fetch(url);
//   const blob = await res.blob();

//   const blobUrl = window.URL.createObjectURL(blob);
//   const link = document.createElement("a");

//   link.href = blobUrl;
//   link.download = fileName;

//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);

//   window.URL.revokeObjectURL(blobUrl);
// };

export const downloadFile = async (url: string, fileName: string) => {
  try {
    const res = await fetch(url, {
      mode: "cors", // IMPORTANT
    });

    if (!res.ok) {
      throw new Error("File fetch failed");
    }

    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed, opening in new tab", err);

    // 🔁 fallback (last resort)
    window.open(url, "_blank");
  }
};


  export const combineDateAndTimeToISO = (date: Date, time: string) => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);

  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);

  return combined.toISOString();
};

export const formatTimeAMPM = (dateString?: string) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
